/** @jsxImportSource @emotion/react */
import { useApolloClient } from '@apollo/client'
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { format as formatDate } from 'date-fns-tz'
import { sortBy } from 'lodash'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import { subMinutes } from 'date-fns'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import Table from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { SubjectTabs } from 'components/SubjectsTabs'
import { DEFAULT_FORMAT } from 'components/DateTimeInput/DateTimeInput'
import { GET_LIST_CRASH_COURSE } from 'queries/crashCourse'
import {
  findAllCrashCourse,
  findAllCrashCourseVariables,
  findAllCrashCourse_findAllCrashCourse_items as CrashCourse,
} from '__generatedTypes__/findAllCrashCourse'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { PageLoader } from 'components/PageLoader'
import * as classes from './styles'

export const CLOSE_REGISTRATION_DELAY_MINUTES = 10

const sortCrashCourses = (crashCourses: CrashCourse[]) => {
  const currentDate = subMinutes(new Date(), CLOSE_REGISTRATION_DELAY_MINUTES)
  const sortedByStartDate = sortBy(crashCourses, 'startDate')

  const upcomingEvents = sortedByStartDate.filter((crashCourse) => new Date(crashCourse.startDate) >= currentDate)
  const pastEvents = sortedByStartDate.filter((crashCourse) => new Date(crashCourse.startDate) < currentDate)

  return upcomingEvents.concat(pastEvents)
}

export const List = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const { navigate, getLink } = useLink()
  const { query } = useApolloClient()
  const [crashCourses, setCrashCourses] = useState<CrashCourse[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const { subjects, selectedSubject, selectSubject } = usePlatformsAndSubjects({
    initialSelectedSubjectId: subjectId ?? undefined,
  })

  useEffect(() => {
    if (!selectedSubject) {
      return
    }

    ;(async () => {
      setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), subjectId: selectedSubject.id }))
      const { data } = await query<findAllCrashCourse, findAllCrashCourseVariables>({
        query: GET_LIST_CRASH_COURSE,
        variables: {
          params: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              subject: {
                id: selectedSubject.id,
              },
            },
          },
        },
        fetchPolicy: 'network-only',
      })
      const sortedCrashCourses = sortCrashCourses(data.findAllCrashCourse.items ?? [])
      setCrashCourses(sortedCrashCourses)
      setIsLoaded(true)
    })()
  }, [selectedSubject])

  const handleAddClick = useCallback(() => {
    if (!selectedSubject) {
      return
    }

    navigate(cmsRoutes.CRASH_COURSE_CREATE, undefined, {
      searchParams: new URLSearchParams({ subjectId: selectedSubject.id, defaultOrder: String(crashCourses.length) }),
    })
  }, [selectedSubject, crashCourses.length])

  if (!isLoaded) {
    return <PageLoader />
  }

  return (
    <div css={classes.wrapper}>
      <Typography variant="title3" css={classes.title}>
        Crash Courses
      </Typography>
      <SubjectTabs subjects={subjects} selectedSubject={selectedSubject} selectSubject={selectSubject} />
      <Table
        columns={[
          {
            title: 'Course Name',
            renderCell: (crashCourse) => crashCourse.courseName,
            flex: 3,
          },
          {
            title: 'Author',
            renderCell: (crashCourse) => crashCourse.author.map((author) => author.fullName).join(', '),
            flex: 2,
          },
          {
            title: 'Price',
            renderCell: (crashCourse) => crashCourse.price ?? '-',
            flex: '0 1 100px',
          },
          {
            title: 'Register Link',
            renderCell: (crashCourse) => (
              <IconButton size="small" href={crashCourse.link} target="_blank" css={classes.link}>
                <InsertLinkIcon />
              </IconButton>
            ),
            flex: '0 1 100px',
          },
          {
            title: 'Course Date',
            renderCell: (crashCourse) => formatDate(new Date(crashCourse.startDate), DEFAULT_FORMAT),
            flex: 2,
          },
          {
            renderCell: (crashCourse) => (
              <Link to={getLink(cmsRoutes.CRASH_COURSE_EDIT, { crashCourseId: crashCourse.id })}>
                <IconButton size="small" css={classes.link}>
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={crashCourses}
      />
      <TableButtons itemName="Course" onAddItem={handleAddClick} disabled={!selectedSubject} />
    </div>
  )
}
